import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { parseSidebarLayout, type SidebarLayout } from "./sidebarLayout";

import { USER_PREFS_QUERY_KEY } from "@/features/preferences/themePreferences";
import { request } from "@/lib/api";

type PrefsResponse = { sidebar?: unknown };

/**
 * The signed-in user's saved left-panel layout, or the default when nothing is
 * saved, the request fails, or the stored value is invalid. Shares the
 * preferences query (and cache) with the theme control.
 */
export function useSidebarLayout(): SidebarLayout {
  const { data } = useQuery({
    queryKey: USER_PREFS_QUERY_KEY,
    queryFn: () => request.get<PrefsResponse>("/users/me/preferences"),
    staleTime: 60_000,
  });
  const raw = (data as PrefsResponse | undefined)?.sidebar;
  return useMemo(() => parseSidebarLayout(raw), [raw]);
}

/** Persist a layout, or `null` to reset to the default. */
export function saveSidebarLayout(
  layout: SidebarLayout | null,
): Promise<PrefsResponse> {
  return request.patch<PrefsResponse>("/users/me/preferences", {
    sidebar: layout,
  });
}
